import { memo, useCallback, useEffect, useState } from 'react'
import styles from './index.module.scss'
import { Button, Modal } from 'antd'
import { NewTrainingGameFormData, TrainingGame } from 'types/games'
import {
    createTrainingGame,
    deleteTrainingGame,
    editTrainingGame,
    getTrainingGameById,
    getTrainingGames,
} from 'services/training-games'
import { TrainingGamesTable } from '../../components/training-games-table'
import { TrainingGameForm } from '../../components/training-game-form'

export const TrainingGames = memo(() => {
    const [games, setGames] = useState<TrainingGame[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [game, setGame] = useState<TrainingGame>({} as TrainingGame)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getTrainingGames()
        }

        getAll()
            .then(data => {
                setGames(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setGame({} as TrainingGame)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getTrainingGameById(id)
                .then(item => {
                    setGame(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteTrainingGame(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: NewTrainingGameFormData) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (game.id) {
                editTrainingGame({
                    ...submitValues,
                    id: game.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createTrainingGame(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, game, toggleModal]
    )

    const editMode = !!game.id
    const modalTitle = editMode
        ? `Edit training game ${game.field?.name} ${game.date} - ${game.timeStart}-${game.timeEnd}`
        : 'Add new training game'

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Training games
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <TrainingGamesTable data={games} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <TrainingGameForm item={game} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})
